(function () {
  const ROOT_SELECTOR = '[data-configurator-v2]';
  const PROPS_SELECTOR = '[data-configurator-v2-props]';

  const getDesignToken = () => {
    const token = new URLSearchParams(window.location.search).get('design');
    return token ? token.trim() : '';
  };

  const hasStep2Param = () => {
    return new URLSearchParams(window.location.search).get('step') === '2';
  };

  const step2Url = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('step', '2');
    return url.toString();
  };

  const productPageUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('step');
    return url.toString();
  };

  const readProps = (root) => {
    const el = root.querySelector(PROPS_SELECTOR);
    if (!el) return {};

    try {
      return JSON.parse(el.textContent || '{}');
    } catch (error) {
      return { parseError: error.message || String(error) };
    }
  };

  const selectedVariantId = (fallback) => {
    const selectedInput = document.querySelector('[name="variant-selection"]:checked');
    if (selectedInput?.value) return selectedInput.value;

    const selectedCard = document.querySelector('[data-variant-card].selected');
    if (selectedCard?.dataset.variantId) return selectedCard.dataset.variantId;

    return fallback || null;
  };

  const currentProductData = (props, variantId) => {
    return props.variantProductData?.[variantId] || props.productData || null;
  };

  const normalizeToken = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/\s+/g, '')
      .replace(/×/g, 'x')
      .trim();
  };

  const modelInfoFromRawProductData = (raw) => {
    if (!raw || typeof raw !== 'object') return {};
    const model = raw.model;
    if (model && typeof model === 'object') {
      return {
        id: model.id || '',
        size: model.size || model.dimensions?.displayName || raw.size || ''
      };
    }

    return {
      id: typeof model === 'string' ? model : '',
      size: raw.size || `${raw.depth || ''}x${raw.length || raw.width || ''}`
    };
  };

  const designInfoFromPayload = (payload) => {
    const designData = payload?.designData || payload || {};
    return {
      model: designData.model || '',
      useCase: designData.useCase || '',
      size: designData.size || `${designData.depth || ''}x${designData.length || ''}`
    };
  };

  const resolveSavedDesignVariantId = (props, payload) => {
    const variants = Object.entries(props.variantProductData || {}).filter(([, raw]) => raw);
    if (!variants.length) return null;

    const design = designInfoFromPayload(payload);
    const designModel = normalizeToken(design.model);
    const designUseCase = normalizeToken(design.useCase);
    const designSize = normalizeToken(design.size);
    const isSizeOnlyModel = designModel === 'signature' || designModel === 'portland';
    if (isSizeOnlyModel && designSize) {
      const sizeMatch = variants.find(([, raw]) => {
        const model = modelInfoFromRawProductData(raw);
        return normalizeToken(model.size) === designSize;
      });
      if (sizeMatch) return sizeMatch[0];
    }

    const scored = variants
      .map(([variantId, raw]) => {
        const model = modelInfoFromRawProductData(raw);
        const modelId = normalizeToken(model.id);
        const modelSize = normalizeToken(model.size);
        let score = 0;

        if (designUseCase && modelId === designUseCase) score += 100;
        if (designModel && modelId === designModel) score += 80;
        if (designModel && designSize && modelId === `${designModel}-${designSize}`) score += 75;
        if (designModel && modelId.startsWith(`${designModel}-`)) score += 25;
        if (designSize && modelSize === designSize) score += 50;

        return { variantId, score };
      })
      .filter((candidate) => candidate.score > 0)
      .sort((a, b) => b.score - a.score);

    return scored[0]?.variantId || null;
  };

  const absoluteUrl = (value) => {
    if (!value) return '';
    if (value.startsWith('//')) return `${window.location.protocol}${value}`;
    return new URL(value, window.location.href).href;
  };

  const endpointBase = (props, root) => {
    return (
      props.apiEndpoint ||
      root.dataset.apiEndpoint ||
      window.STUDIO_SHED_DESIGN_API_ENDPOINT ||
      'https://design.studio-shed.com/api/designs'
    ).replace(/\/+$/, '');
  };

  const fetchDesignPayload = async (props, root, token) => {
    if (!token) return null;

    const headers = { Accept: 'application/json' };
    const apiToken = props.apiToken || root.dataset.apiToken || window.STUDIO_SHED_DESIGN_API_TOKEN || '';
    if (apiToken) headers.Authorization = `Bearer ${apiToken}`;

    const response = await fetch(`${endpointBase(props, root)}/${encodeURIComponent(token)}/data`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`Design payload request failed with ${response.status}`);
    }

    return response.json();
  };

  const setActive = (root, active) => {
    root.hidden = !active;
    document.body.classList.toggle('configurator-v2-active', active);
  };

  const pushStep2History = (root) => {
    if (!window.history?.pushState || hasStep2Param()) {
      root.dataset.configuratorV2History = hasStep2Param() ? 'existing' : '';
      return;
    }

    window.history.pushState({ configuratorV2Step: 2 }, '', step2Url());
    root.dataset.configuratorV2History = 'pushed';
  };

  const replaceProductPageUrl = () => {
    if (!window.history?.replaceState || !hasStep2Param()) return;
    window.history.replaceState(window.history.state || {}, '', productPageUrl());
  };

  const closeConfigurator = (root) => {
    const pushedByLauncher = root.dataset.configuratorV2History === 'pushed';
    root.dataset.configuratorV2History = '';
    setActive(root, false);

    if (pushedByLauncher && hasStep2Param() && window.history?.back) {
      window.history.back();
      return;
    }

    replaceProductPageUrl();
  };

  const openConfigurator = (root) => {
    pushStep2History(root);
    setActive(root, true);
  };

  const renderFallback = (root, message, token) => {
    const returnHref = token
      ? `${window.STUDIO_SHED_APP_PROXY_BASE || '/tools/design'}/${encodeURIComponent(token)}`
      : '/pages/consultation';

    root.innerHTML = `
      <div class="configurator-v2-shell" role="dialog" aria-modal="true" aria-labelledby="configurator-v2-title">
        <div class="configurator-v2-panel">
          <button class="configurator-v2-close" type="button" aria-label="Close">&times;</button>
          <p class="configurator-v2-eyebrow">Design Portal</p>
          <h2 id="configurator-v2-title">The editor is not available yet.</h2>
          <p>${message}</p>
          <a class="btn btn-primary" href="${returnHref}">${token ? 'Back to saved design' : 'Schedule a consultation'}</a>
        </div>
      </div>
    `;

    root.querySelector('.configurator-v2-close')?.addEventListener('click', () => {
      closeConfigurator(root);
    });
  };

  const injectFallbackStyles = () => {
    if (document.getElementById('configurator-v2-fallback-styles')) return;

    const style = document.createElement('style');
    style.id = 'configurator-v2-fallback-styles';
    style.textContent = `
      body.configurator-v2-active { overflow: hidden; }
      .configurator-v2-root[hidden] { display: none !important; }
      .configurator-v2-root {
        position: fixed;
        inset: 0;
        z-index: 2147483000;
        background: #fff;
        height: 100vh;
        height: 100dvh;
        overflow: hidden;
      }
      .configurator-v2-shell {
        min-height: 100%;
        display: grid;
        place-items: center;
        padding: 24px;
        background: #f7f5ef;
      }
      .configurator-v2-panel {
        width: min(560px, 100%);
        background: #fff;
        border: 1px solid #ddd8cf;
        border-radius: 8px;
        padding: 28px;
        position: relative;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.14);
      }
      .configurator-v2-close {
        position: absolute;
        top: 12px;
        right: 12px;
        width: 36px;
        height: 36px;
        border: 1px solid #d6d0c6;
        border-radius: 50%;
        background: #fff;
        line-height: 1;
        font-size: 24px;
      }
      .configurator-v2-eyebrow {
        margin: 0 0 8px;
        color: #666;
        font-size: 12px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .configurator-v2-panel h2 {
        margin: 0 40px 12px 0;
        font-size: 28px;
        line-height: 1.15;
      }
      .configurator-v2-panel p:not(.configurator-v2-eyebrow) {
        margin: 0 0 20px;
        color: #4d4d4d;
      }
    `;

    document.head.appendChild(style);
  };

  const loadMount = async (root) => {
    if (window.StudioHomeConfiguratorV2?.mountConfigurator) {
      return window.StudioHomeConfiguratorV2.mountConfigurator;
    }
    if (typeof window.mountConfigurator === 'function') {
      return window.mountConfigurator;
    }

    const bundleUrl = absoluteUrl(root.dataset.bundleUrl);
    if (!bundleUrl) {
      throw new Error('Configurator v2 bundle URL is not configured.');
    }

    const module = await import(bundleUrl);
    const mountConfigurator = module.mountConfigurator || module.default?.mountConfigurator;
    if (typeof mountConfigurator !== 'function') {
      throw new Error('Configurator v2 bundle did not export mountConfigurator.');
    }
    return mountConfigurator;
  };

  const launch = async (root, props, token = '') => {
    injectFallbackStyles();
    if (token) {
      setActive(root, true);
    } else {
      openConfigurator(root);
    }

    let payload = null;
    if (token) {
      try {
        payload = await fetchDesignPayload(props, root, token);
      } catch (error) {
        renderFallback(root, 'We could not load that saved design. Please return to the portal and try again.', token);
        return;
      }
    }

    const fallbackVariantId = selectedVariantId(props.selectedVariantId);
    const variantId = token && payload
      ? resolveSavedDesignVariantId(props, payload) || fallbackVariantId
      : fallbackVariantId;
    const productData = currentProductData(props, variantId);
    if (!productData) {
      renderFallback(root, 'This product does not have configurator JSON for this saved design.', token);
      return;
    }

    try {
      window.SH_CONFIGURATOR_V2_ASSET_BASE_URL = absoluteUrl(props.assetBaseUrl || root.dataset.assetBaseUrl || '');
      const mountConfigurator = await loadMount(root);
      root.replaceChildren();
      await mountConfigurator(root, {
        ...props,
        apiEndpoint: endpointBase(props, root),
        apiToken: props.apiToken || root.dataset.apiToken || window.STUDIO_SHED_DESIGN_API_TOKEN || null,
        appProxyBase: window.STUDIO_SHED_APP_PROXY_BASE || props.appProxyBase || '/tools/design',
        productData,
        selectedVariantId: variantId,
        getSelectedVariantId: token ? () => variantId : () => selectedVariantId(variantId),
        designToken: token || null,
        payload,
        locked: Boolean(payload?.locked),
        customer: payload?.customer || null,
        mode: token ? 'edit' : 'new',
        onClose: () => closeConfigurator(root)
      });
    } catch (error) {
      renderFallback(root, error.message || 'The v2 editor bundle is not available yet.', token);
    }
  };

  const init = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    const props = readProps(root);
    const token = getDesignToken();

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-design-your-own]');
      if (!button) return;
      event.preventDefault();
      let initialStyle = button.dataset.styleId || null;
      if (!initialStyle) {
        try { initialStyle = sessionStorage.getItem('sh_pdp_style'); } catch (e) {}
      } else {
        try { sessionStorage.setItem('sh_pdp_style', initialStyle); } catch (e) {}
      }
      launch(root, initialStyle ? { ...props, initialStyle } : props);
    });

    if (token) {
      launch(root, props, token);
    }

    window.addEventListener('popstate', () => {
      if (root.hidden || hasStep2Param()) return;
      root.dataset.configuratorV2History = '';
      setActive(root, false);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
